import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import PageHeader from "../../../../components/common/PageHeader";
import FormField from "../../../../components/common/FormField";
import PasswordField from "../../../../components/common/PasswordField";
import ChipAutocomplete from "../../../../components/common/ChipAutocomplete";
import useFormValidation from "../../../../hooks/useFormValidation";
import {
  email,
  matchField,
  minLength,
  mobile,
  required,
  sanitizeNationalPhone,
  toApiMobileNumber,
} from "../../../../utils/formValidation";
import { USER_TYPES } from "../../constants";
import { getTenantClientId } from "../../../../config/loginTenants";
import { systemUserService } from "../../services/systemUser.service";
import { tenantUserService } from "../../services/tenantUser.service";
import { roleService } from "../../services/role.service";
import {
  catalogLabels,
  filterByCatalog,
} from "../../../../helpers/catalogFilter.helper";
import { tenantRoleService } from "../../../role-management/services/tenantRole.service";
import { tenantClientService } from "../../../client-management/services/tenantClient.service";
import { normalizeClientList } from "../../../client-management/utils/normalizeClient";
import { normalizeRoleList } from "../../../role-management/utils/normalizeRole";
import { appActions } from "../../../../redux/actions";
import { getApiErrorMessage } from "../../../../helpers";
import useAuth from "../../../../hooks/useAuth";

const systemSchema = {
  firstname: [required("First name is required.")],
  lastname: [],
  username: [required("Username is required.")],
  email: [required("Email is required."), email()],
  mobile: [required("Mobile is required."), mobile()],
  password: [
    required("Password is required."),
    minLength(4, "Password must be at least 4 characters."),
  ],
  confirmPassword: [
    required("Confirm password is required."),
    matchField("password", "Passwords do not match."),
  ],
};

const tenantSchema = {
  firstname: [required("First name is required.")],
  lastname: [],
  username: [required("Username is required.")],
  email: [required("Email is required."), email()],
  mobile: [required("Mobile is required."), mobile()],
  password: [
    required("Password is required."),
    minLength(4, "Password must be at least 4 characters."),
  ],
  confirmPassword: [
    required("Confirm password is required."),
    matchField("password", "Passwords do not match."),
  ],
};

const UserCreatePage = ({ userType = USER_TYPES.TENANT }) => {
  const isSystem = userType === USER_TYPES.SYSTEM;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { activeTenantId, tenantId: authTenantId } = useAuth();
  const tenantId = activeTenantId || authTenantId;
  const listPath = isSystem ? "/systemusers" : "/tenantusers";

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const schema = isSystem ? systemSchema : tenantSchema;
  const initialValues = {
    firstname: "",
    lastname: "",
    username: "",
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
  };

  const { values, getFieldProps, validateAll, setFieldValue, isValid } =
    useFormValidation(initialValues, schema);

  useEffect(() => {
    let cancelled = false;
    const loadRoles = async () => {
      setRolesLoading(true);
      try {
        if (isSystem) {
          const list = await roleService.getAllRoles();
          if (!cancelled) setRoles(list || []);
        } else {
          const payload = await tenantRoleService.getAllTenantRoles(tenantId);
          if (!cancelled) {
            setRoles(
              normalizeRoleList(payload)
                .map((role) => role.name)
                .filter(Boolean)
            );
          }
        }
      } catch {
        if (!cancelled) setRoles([]);
      } finally {
        if (!cancelled) setRolesLoading(false);
      }
    };
    loadRoles();
    return () => {
      cancelled = true;
    };
  }, [isSystem, tenantId]);

  useEffect(() => {
    if (isSystem || !tenantId) return undefined;
    let cancelled = false;
    const loadClients = async () => {
      setClientsLoading(true);
      try {
        const payload = await tenantClientService.getAllTenantClients(tenantId);
        if (cancelled) return;
        const list = normalizeClientList(payload)
          .filter(
            (client) =>
              client.clientId &&
              client.enabled !== false &&
              client.allowUserCreation
          )
          .sort((a, b) =>
            String(a.name || a.clientId || "").localeCompare(
              String(b.name || b.clientId || ""),
              undefined,
              { sensitivity: "base" }
            )
          );
        setClients(list);

        const preferred =
          list.find(
            (client) => client.clientId === getTenantClientId(tenantId)
          ) || list[0];
        setSelectedClientId(preferred?.clientId || "");
      } catch {
        if (!cancelled) {
          setClients([]);
          setSelectedClientId(getTenantClientId(tenantId) || "");
        }
      } finally {
        if (!cancelled) setClientsLoading(false);
      }
    };
    loadClients();
    return () => {
      cancelled = true;
    };
  }, [isSystem, tenantId]);

  const roleOptions = useMemo(() => catalogLabels(roles), [roles]);

  useEffect(() => {
    if (rolesLoading) return;
    setSelectedRoles((prev) => filterByCatalog(prev, roles));
  }, [roles, rolesLoading]);

  const selectedClient = useMemo(
    () => clients.find((client) => client.clientId === selectedClientId) || null,
    [clients, selectedClientId]
  );

  const canSubmit = Boolean(
    !saving &&
      selectedRoles.length &&
      isValid &&
      values.password &&
      values.confirmPassword &&
      values.password === values.confirmPassword &&
      (isSystem || selectedClientId)
  );

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    const { isValid: ok, errors: nextErrors } = validateAll();
    if (!ok) {
      setFormError(
        Object.values(nextErrors).find(Boolean) ||
          "Please correct the highlighted fields."
      );
      return;
    }
    if (!selectedRoles.length) {
      setFormError("Select at least one role.");
      return;
    }

    setFormError("");
    setSaving(true);
    const mobileValue = toApiMobileNumber(values.mobile);
    try {
      if (isSystem) {
        await systemUserService.createSystemUser({
          firstname: values.firstname.trim(),
          lastname: values.lastname.trim(),
          username: values.username.trim(),
          email: values.email.trim(),
          mobile: mobileValue,
          password: values.password,
          roles: selectedRoles,
        });
        dispatch(appActions.apiSuccessMessage("System user created successfully."));
      } else {
        const clientId = selectedClientId || getTenantClientId(tenantId);
        if (!clientId) {
          throw new Error("Select a client that allows user creation.");
        }
        if (selectedClient && !selectedClient.allowUserCreation) {
          throw new Error(
            "User creation is forbidden for the selected client. Choose a client with Allow User Creation enabled (Client Management), or enable it on this client."
          );
        }
        await tenantUserService.createTenantUser(
          {
            firstname: values.firstname.trim(),
            lastname: values.lastname.trim(),
            username: values.username.trim(),
            email: values.email.trim(),
            mobile: mobileValue,
            password: values.password,
            roles: selectedRoles,
            clientId,
          },
          tenantId
        );
        dispatch(appActions.apiSuccessMessage("User created successfully."));
      }
      navigate(listPath);
    } catch (err) {
      const message = getApiErrorMessage(err);
      if (/forbidden via this client/i.test(message)) {
        setFormError(
          "User creation is forbidden for this client. In Client Management, pick/enable a client with Allow User Creation, then select that client here."
        );
      } else {
        setFormError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: "100%", alignSelf: "stretch" }}>
      <Button
        startIcon={<ArrowBackOutlinedIcon />}
        onClick={() => navigate(listPath)}
        sx={{ mb: 0.75, px: 0.5 }}
      >
        Back to users
      </Button>
      <PageHeader
        title="Add User"
        subtitle={
          isSystem
            ? "Create a system user account."
            : "Create a tenant user account."
        }
        sx={{ mb: 2 }}
      />

      <Paper
        elevation={0}
        component="form"
        noValidate
        onSubmit={handleSubmit}
        sx={{
          width: "100%",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2.5,
          overflow: "hidden",
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        }}
      >
        <Box sx={{ p: { xs: 2.5, md: 3 } }}>
          {formError ? (
            <Alert
              severity="error"
              sx={{ mb: 2.5 }}
              onClose={() => setFormError("")}
            >
              {formError}
            </Alert>
          ) : null}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr",
                lg: "1fr 1fr 1fr",
              },
              gap: 2.25,
              alignItems: "start",
            }}
          >
            <FormField
              label="First Name"
              required
              {...getFieldProps("firstname")}
            />
            <FormField label="Last Name" {...getFieldProps("lastname")} />
            <FormField
              label="Username"
              required
              {...getFieldProps("username")}
            />
            <FormField
              label="Email"
              required
              type="email"
              {...getFieldProps("email")}
            />
            <FormField
              label="Mobile"
              required
              {...getFieldProps("mobile")}
              onChange={(event) =>
                setFieldValue(
                  "mobile",
                  sanitizeNationalPhone(event.target.value)
                )
              }
              placeholder="10-digit mobile"
              inputProps={{ inputMode: "numeric", maxLength: 10 }}
            />
            <PasswordField
              label="Password"
              required
              {...getFieldProps("password")}
            />
            <PasswordField
              label="Confirm Password"
              required
              {...getFieldProps("confirmPassword")}
            />
            <ChipAutocomplete
              label="Roles"
              required
              freeSolo={!isSystem}
              options={roleOptions}
              value={selectedRoles}
              onChange={setSelectedRoles}
              loading={rolesLoading}
              disabled={saving}
              helperText={
                selectedRoles.length
                  ? isSystem
                    ? "Only IDP system roles are listed."
                    : " "
                  : "Select at least one role."
              }
            />
            {!isSystem ? (
              <FormControl
                size="small"
                required
                fullWidth
                disabled={saving || clientsLoading || !clients.length}
                error={!clientsLoading && !clients.length}
              >
                <InputLabel id="create-user-client-label">Client</InputLabel>
                <Select
                  labelId="create-user-client-label"
                  label="Client"
                  value={selectedClientId}
                  onChange={(event) => setSelectedClientId(event.target.value)}
                  renderValue={(value) => {
                    const client = clients.find(
                      (item) => item.clientId === value
                    );
                    return client?.name || client?.clientId || value || "";
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 360,
                        mt: 0.5,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
                      },
                    },
                  }}
                >
                  {clients.map((client) => (
                    <MenuItem
                      key={client.clientId}
                      value={client.clientId}
                      sx={{ py: 1.1, px: 2 }}
                    >
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                        {client.name || client.clientId}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText sx={{ mx: 0 }}>
                  {!clientsLoading && !clients.length
                    ? "No clients allow user creation. Enable it in Client Management."
                    : "Only clients that allow user creation are listed."}
                </FormHelperText>
              </FormControl>
            ) : null}
          </Box>
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          justifyContent="flex-end"
          sx={{
            px: { xs: 2.5, md: 3 },
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "grey.50",
          }}
        >
          <Button
            onClick={() => navigate(listPath)}
            disabled={saving}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
            sx={{ minWidth: 132 }}
          >
            {saving ? "Creating..." : "Create User"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default UserCreatePage;
